import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Tabs,
    Tab,
    Stack,
    Chip,
    IconButton,
    Tooltip,
    LinearProgress,
    Paper,
    Divider,
    Avatar,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from '@mui/material';
import {
    PhotoCamera,
    CloudUpload,
    Psychology,
    ModelTraining,
    Visibility,
    ArrowBack,
    Image as ImageIcon,
    Label,
    Info,
    CheckCircle,
    Error as ErrorIcon,
    HourglassEmpty,
    Storage,
    Delete as DeleteIcon,
    Warning,
    PlayArrow,
    Refresh,
    Autorenew,
    Stop,
    PowerSettingsNew,
    Download
} from '@mui/icons-material';
import MainCard from 'ui-component/cards/MainCard';
import { openSnackbar } from 'store/slices/snackbar';
import DatasetUploadDialog from './DatasetUploadDialog';
import DatasetDetailsDialog from './DatasetDetailsDialog';
import TrainingConfigDialog from './TrainingConfigDialog';
import TrainingJobDetailsDialog from './TrainingJobDetailsDialog';
import ModelDetailsDialog from './ModelDetailsDialog';
import axiosDirectApi from 'utils/axiosDirectApi';

// Tab Panel Component
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`cv-tabpanel-${index}`} aria-labelledby={`cv-tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const CVModulePage = ({ projectName, projectSubname, onBack }) => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.account.user);
    const [currentTab, setCurrentTab] = useState(0);
    const [datasets, setDatasets] = useState([]);
    const [trainingJobs, setTrainingJobs] = useState([]);
    const [models, setModels] = useState([]);
    const [selectedModelType, setSelectedModelType] = useState('yolov8');
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [datasetToDelete, setDatasetToDelete] = useState(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [selectedDatasetId, setSelectedDatasetId] = useState(null);
    const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
    const [datasetToTrain, setDatasetToTrain] = useState(null);
    const [trainingJobDetailsOpen, setTrainingJobDetailsOpen] = useState(false);
    const [selectedTrainingJob, setSelectedTrainingJob] = useState(null);
    const [modelDetailsOpen, setModelDetailsOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [containerStatuses, setContainerStatuses] = useState({});
    const [selectedInferenceModel, setSelectedInferenceModel] = useState(null);
    const [inferenceImage, setInferenceImage] = useState(null);
    const [inferenceResult, setInferenceResult] = useState(null);
    const [inferenceLoading, setInferenceLoading] = useState(false);
    const [showApiDocs, setShowApiDocs] = useState(false);
    const [annotatedImageUrl, setAnnotatedImageUrl] = useState(null);

    // Supported AI CV model types
    const modelTypes = [
        { value: 'yolov8', label: 'YOLOv8 (Object Detection)' },
        { value: 'yolov5', label: 'YOLOv5 (Object Detection)' },
        { value: 'faster-rcnn', label: 'Faster R-CNN (Object Detection)' },
        { value: 'mask-rcnn', label: 'Mask R-CNN (Instance Segmentation)' },
        { value: 'resnet', label: 'ResNet (Classification)' },
        { value: 'efficientnet', label: 'EfficientNet (Classification)' },
        { value: 'unet', label: 'U-Net (Semantic Segmentation)' },
        { value: 'detr', label: 'DETR (Transformer Detection)' }
    ];

    useEffect(() => {
        loadDatasets();
        loadTrainingJobs();
        loadModels();
    }, [projectName]);

    // Auto-refresh training jobs when on Training Jobs tab
    useEffect(() => {
        // Only poll when on Training Jobs tab
        if (currentTab !== 1) return;

        // Check if there are running or pending jobs
        const hasActiveJobs = trainingJobs.some((job) => job.status === 'running' || job.status === 'pending');

        if (!hasActiveJobs) return;

        // Poll every 5 seconds
        const pollInterval = setInterval(() => {
            loadTrainingJobs();
        }, 5000);

        // Cleanup on unmount or tab change
        return () => {
            clearInterval(pollInterval);
        };
    }, [currentTab, trainingJobs]);

    const loadDatasets = async () => {
        try {
            setLoading(true);
            const response = await axiosDirectApi.get('/cv/datasets', {
                params: { project_id: projectName }
            });
            setDatasets(response.data);
        } catch (error) {
            console.error('Failed to load datasets:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadTrainingJobs = async () => {
        try {
            const response = await axiosDirectApi.get('/cv/training/jobs', {
                params: { project_id: projectName }
            });
            setTrainingJobs(response.data);
        } catch (error) {
            console.error('Failed to load training jobs:', error);
        }
    };

    const loadModels = async () => {
        try {
            const response = await axiosDirectApi.get('/cv/models', {
                params: { project_id: projectName }
            });
            setModels(response.data);

            // Fetch container status for deployed models
            const deployedModels = response.data.filter((m) => m.is_deployed);
            if (deployedModels.length > 0) {
                const statusPromises = deployedModels.map((model) =>
                    axiosDirectApi
                        .get(`/cv/models/${model.id}/container-status`)
                        .then((res) => ({ modelId: model.id, status: res.data }))
                        .catch((err) => ({ modelId: model.id, status: { container: { deployed: false, status: 'error' } } }))
                );

                const statuses = await Promise.all(statusPromises);
                const statusMap = {};
                statuses.forEach(({ modelId, status }) => {
                    statusMap[modelId] = status.container;
                });
                setContainerStatuses(statusMap);
            }
        } catch (error) {
            console.error('Failed to load models:', error);
        }
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleCreateDataset = () => {
        setUploadDialogOpen(true);
    };

    const handleUploadSuccess = (datasetId) => {
        dispatch(
            openSnackbar({
                open: true,
                message: 'Dataset đã được tạo và tải lên thành công!',
                variant: 'alert',
                alert: { color: 'success' },
                close: true
            })
        );
        loadDatasets();
    };

    const handleDeleteClick = (dataset) => {
        setDatasetToDelete(dataset);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!datasetToDelete) return;

        try {
            await axiosDirectApi.delete(`/cv/datasets/${datasetToDelete.id}`, {
                params: { hard_delete: true } // Hard delete - remove data from storage
            });

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Dataset "${datasetToDelete.name}" đã được xóa thành công!`,
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );

            // Close dialog and reload datasets
            setDeleteDialogOpen(false);
            setDatasetToDelete(null);
            loadDatasets();
        } catch (error) {
            console.error('Delete dataset error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi xóa dataset',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setDatasetToDelete(null);
    };

    const handleStartTraining = () => {
        const selectedModel = modelTypes.find((m) => m.value === selectedModelType);
        dispatch(
            openSnackbar({
                open: true,
                message: `Tính năng training với ${selectedModel?.label} đang được phát triển`,
                variant: 'alert',
                alert: { color: 'info' },
                close: true
            })
        );
    };

    const handleModelTypeChange = (event) => {
        setSelectedModelType(event.target.value);
    };

    const handleViewDetails = (dataset) => {
        setSelectedDatasetId(dataset.id);
        setDetailsDialogOpen(true);
    };

    const handleTrainClick = (dataset) => {
        setDatasetToTrain(dataset);
        setTrainingDialogOpen(true);
    };

    const handleTrainConfirm = async (jobData) => {
        if (!datasetToTrain) return;

        try {
            // Step 1: Create training job
            const response = await axiosDirectApi.post('/cv/training/jobs', jobData);
            const jobId = response.data.id;

            // Step 2: Auto-start training job
            try {
                await axiosDirectApi.post(`/cv/training/jobs/${jobId}/start`);

                dispatch(
                    openSnackbar({
                        open: true,
                        message: `Đã tạo và start training job "${jobData.name}" thành công! Job ID: ${jobId.substring(0, 8)}...`,
                        variant: 'alert',
                        alert: { color: 'success' },
                        close: true
                    })
                );
            } catch (startError) {
                console.warn('Failed to auto-start training job:', startError);
                dispatch(
                    openSnackbar({
                        open: true,
                        message: `Training job đã tạo nhưng không thể auto-start. Vui lòng start manually.`,
                        variant: 'alert',
                        alert: { color: 'warning' },
                        close: true
                    })
                );
            }

            setTrainingDialogOpen(false);
            setDatasetToTrain(null);

            // Reload training jobs to show new job
            await loadTrainingJobs();

            // Switch to Training Jobs tab to see the new job
            setCurrentTab(1);
        } catch (error) {
            console.error('Create training job error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi tạo training job',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    const handleTrainCancel = () => {
        setTrainingDialogOpen(false);
        setDatasetToTrain(null);
    };

    const handleStartJob = async (job) => {
        try {
            await axiosDirectApi.post(`/cv/training/jobs/${job.id}/start`);

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Training job "${job.name}" đã được start!`,
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );

            // Reload training jobs
            await loadTrainingJobs();
        } catch (error) {
            console.error('Start training job error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi start training job',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    const handleRestartJob = async (job) => {
        try {
            // Restart = update job status to pending, then start
            await axiosDirectApi.patch(`/cv/training/jobs/${job.id}`, {
                status: 'pending',
                error_message: null,
                progress: 0
            });

            await axiosDirectApi.post(`/cv/training/jobs/${job.id}/start`);

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Đã restart training job "${job.name}"!`,
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );

            await loadTrainingJobs();
        } catch (error) {
            console.error('Restart training job error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi restart training job',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    const handleDeleteJob = async (job) => {
        if (!window.confirm(`Xác nhận xóa training job "${job.name}"?`)) {
            return;
        }

        try {
            await axiosDirectApi.delete(`/cv/training/jobs/${job.id}`);

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Đã xóa training job "${job.name}"`,
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );

            await loadTrainingJobs();
        } catch (error) {
            console.error('Delete training job error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi xóa training job',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    const handleViewJobDetails = (job) => {
        setSelectedTrainingJob(job);
        setTrainingJobDetailsOpen(true);
    };

    const handleViewModelDetails = (model) => {
        setSelectedModel(model);
        setModelDetailsOpen(true);
    };

    const handleDeployModel = async (model) => {
        try {
            // Send deployment config in request body
            await axiosDirectApi.post(`/cv/models/${model.id}/deploy`, {
                deployment_type: 'fastapi',
                config: {}
            });

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Model "${model.name}" đã được deploy thành công!`,
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );

            await loadModels();
        } catch (error) {
            console.error('Deploy model error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi deploy model',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    const handleUndeployModel = async (model) => {
        try {
            await axiosDirectApi.delete(`/cv/models/${model.id}/deploy`);

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Model "${model.name}" đã được undeploy!`,
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );

            await loadModels();
        } catch (error) {
            console.error('Undeploy model error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi undeploy model',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    const handleStartContainer = async (model) => {
        try {
            await axiosDirectApi.post(`/cv/models/${model.id}/container/start`);

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Container đã được start thành công!`,
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );

            await loadModels();
        } catch (error) {
            console.error('Start container error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi start container',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    const handleStopContainer = async (model) => {
        try {
            await axiosDirectApi.post(`/cv/models/${model.id}/container/stop`);

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Container đã được stop thành công!`,
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );

            await loadModels();
        } catch (error) {
            console.error('Stop container error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi stop container',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setInferenceImage(file);
            setInferenceResult(null);
        }
    };

    const drawBoundingBoxes = (image, detections) => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const img = new Image();
            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;

                // Draw original image
                ctx.drawImage(img, 0, 0);

                // Draw bounding boxes
                detections.forEach((detection, idx) => {
                    const [x1, y1, x2, y2] = detection.bbox;
                    const width = x2 - x1;
                    const height = y2 - y1;

                    // Generate color based on class
                    const hue = (idx * 137.5) % 360;
                    const color = `hsl(${hue}, 70%, 50%)`;

                    // Draw box
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 3;
                    ctx.strokeRect(x1, y1, width, height);

                    // Draw label background
                    const label = `${detection.class_name} ${(detection.confidence * 100).toFixed(1)}%`;
                    ctx.font = 'bold 16px Arial';
                    const textMetrics = ctx.measureText(label);
                    const textHeight = 20;

                    ctx.fillStyle = color;
                    ctx.fillRect(x1, y1 - textHeight - 4, textMetrics.width + 10, textHeight + 4);

                    // Draw label text
                    ctx.fillStyle = 'white';
                    ctx.fillText(label, x1 + 5, y1 - 8);
                });

                // Convert to blob URL
                canvas.toBlob((blob) => {
                    const url = URL.createObjectURL(blob);
                    resolve(url);
                });
            };

            img.src = URL.createObjectURL(image);
        });
    };

    const handleRunInference = async () => {
        if (!selectedInferenceModel || !inferenceImage) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Vui lòng chọn model và upload ảnh',
                    variant: 'alert',
                    alert: { color: 'warning' },
                    close: true
                })
            );
            return;
        }

        setInferenceLoading(true);
        setAnnotatedImageUrl(null);

        try {
            const shortId = selectedInferenceModel.id.substring(0, 8);
            const formData = new FormData();
            formData.append('file', inferenceImage);
            formData.append('conf', '0.25');
            formData.append('iou', '0.45');

            const response = await fetch(`https://datahub.n2nai.io/models/${shortId}/predict`, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();
            setInferenceResult(result);

            // Draw bounding boxes on image
            if (result.detections && result.detections.length > 0) {
                const annotatedUrl = await drawBoundingBoxes(inferenceImage, result.detections);
                setAnnotatedImageUrl(annotatedUrl);
            } else {
                // No detections, show original image
                setAnnotatedImageUrl(URL.createObjectURL(inferenceImage));
            }

            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Inference thành công!',
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );
        } catch (error) {
            console.error('Inference error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Inference thất bại: ' + (error.message || 'Unknown error'),
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        } finally {
            setInferenceLoading(false);
        }
    };

    const handleDeleteModel = async (model) => {
        if (!window.confirm(`Xác nhận xóa model "${model.name}" (v${model.version})?`)) {
            return;
        }

        try {
            await axiosDirectApi.delete(`/cv/models/${model.id}`);

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Đã xóa model "${model.name}"`,
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: true
                })
            );

            await loadModels();
        } catch (error) {
            console.error('Delete model error:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.response?.data?.detail || 'Lỗi khi xóa model',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: true
                })
            );
        }
    };

    return (
        <MainCard
            title={
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton
                        onClick={onBack}
                        sx={{
                            bgcolor: 'primary.lighter',
                            '&:hover': { bgcolor: 'primary.light' }
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 600 }}>
                            AI Computer Vision
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {projectSubname} • Quản lý datasets, training và models
                        </Typography>
                    </Box>
                </Stack>
            }
        >
            <Stack spacing={3}>
                {/* Statistics Cards */}
                <Box>
                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                        {/* Datasets Card */}
                        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 220 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    bgcolor: 'primary.lighter',
                                    border: '1px solid',
                                    borderColor: 'primary.light',
                                    borderRadius: 2,
                                    height: '100%'
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Avatar
                                            sx={{
                                                bgcolor: 'primary.main',
                                                width: 48,
                                                height: 48
                                            }}
                                        >
                                            <Storage />
                                        </Avatar>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                            {datasets.length}
                                        </Typography>
                                    </Stack>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Datasets
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Sẵn sàng training
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Box>

                        {/* Training Jobs Card */}
                        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 220 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    bgcolor: 'secondary.lighter',
                                    border: '1px solid',
                                    borderColor: 'secondary.light',
                                    borderRadius: 2,
                                    height: '100%'
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Avatar
                                            sx={{
                                                bgcolor: 'secondary.main',
                                                width: 48,
                                                height: 48
                                            }}
                                        >
                                            <ModelTraining />
                                        </Avatar>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                                            {trainingJobs.length}
                                        </Typography>
                                    </Stack>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Training Jobs
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Đang xử lý
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Box>

                        {/* Models Card */}
                        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 220 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    bgcolor: 'success.lighter',
                                    border: '1px solid',
                                    borderColor: 'success.light',
                                    borderRadius: 2,
                                    height: '100%'
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Avatar
                                            sx={{
                                                bgcolor: 'success.main',
                                                width: 48,
                                                height: 48
                                            }}
                                        >
                                            <Psychology />
                                        </Avatar>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                                            {models.length}
                                        </Typography>
                                    </Stack>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Trained Models
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Sẵn sàng deploy
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Box>

                        {/* Deployed Card */}
                        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 220 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    bgcolor: 'warning.lighter',
                                    border: '1px solid',
                                    borderColor: 'warning.light',
                                    borderRadius: 2,
                                    height: '100%'
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Avatar
                                            sx={{
                                                bgcolor: 'warning.main',
                                                width: 48,
                                                height: 48
                                            }}
                                        >
                                            <Visibility />
                                        </Avatar>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                            0
                                        </Typography>
                                    </Stack>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Deployed
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Đang phục vụ
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Box>
                    </Stack>
                </Box>

                {/* Tabs Navigation - Clearly Below Statistics */}
                <Box>
                    <Card
                        elevation={2}
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                                bgcolor: 'background.paper'
                            }}
                        >
                            <Tabs
                                value={currentTab}
                                onChange={handleTabChange}
                                sx={{
                                    px: 3,
                                    '& .MuiTab-root': {
                                        minHeight: 64,
                                        textTransform: 'none',
                                        fontSize: '0.95rem',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        '&.Mui-selected': {
                                            color: 'primary.main'
                                        }
                                    },
                                    '& .MuiTabs-indicator': {
                                        height: 3,
                                        borderRadius: '3px 3px 0 0'
                                    }
                                }}
                            >
                                <Tab icon={<Storage sx={{ fontSize: 22 }} />} iconPosition="start" label="Datasets" />
                                <Tab
                                    icon={
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <ModelTraining sx={{ fontSize: 22 }} />
                                            {trainingJobs.some((job) => job.status === 'running' || job.status === 'pending') && (
                                                <Autorenew
                                                    sx={{
                                                        fontSize: 16,
                                                        color: 'success.main',
                                                        animation: 'spin 2s linear infinite',
                                                        '@keyframes spin': {
                                                            '0%': { transform: 'rotate(0deg)' },
                                                            '100%': { transform: 'rotate(360deg)' }
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    }
                                    iconPosition="start"
                                    label="Training Jobs"
                                />
                                <Tab icon={<Psychology sx={{ fontSize: 22 }} />} iconPosition="start" label="Models" />
                                <Tab icon={<Visibility sx={{ fontSize: 22 }} />} iconPosition="start" label="Inference" />
                            </Tabs>
                        </Box>

                        {/* Datasets Tab */}
                        <TabPanel value={currentTab} index={0}>
                            <Stack spacing={2.5}>
                                {/* Header */}
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                            Datasets
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Quản lý dữ liệu training cho AI models
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<CloudUpload />}
                                        onClick={handleCreateDataset}
                                        sx={{
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            px: 3,
                                            py: 1.2,
                                            fontWeight: 600,
                                            boxShadow: 2,
                                            '&:hover': {
                                                bgcolor: 'primary.dark',
                                                boxShadow: 4
                                            }
                                        }}
                                    >
                                        Tạo Dataset
                                    </Button>
                                </Stack>

                                <Divider />

                                {/* Content */}
                                {loading ? (
                                    <Box sx={{ width: '100%' }}>
                                        <LinearProgress />
                                    </Box>
                                ) : datasets.length === 0 ? (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            py: 8,
                                            px: 4,
                                            textAlign: 'center',
                                            bgcolor: 'grey.50',
                                            borderRadius: 2,
                                            border: '2px dashed',
                                            borderColor: 'grey.300'
                                        }}
                                    >
                                        <Stack spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    bgcolor: 'primary.lighter',
                                                    color: 'primary.main'
                                                }}
                                            >
                                                <Storage sx={{ fontSize: 40 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                                                    Chưa có datasets
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                                                    Tạo dataset đầu tiên để bắt đầu training AI models. Hỗ trợ nhiều định dạng: YOLO, COCO,
                                                    Pascal VOC, ImageNet.
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                startIcon={<CloudUpload />}
                                                onClick={handleCreateDataset}
                                                sx={{ px: 4, py: 1.5, mt: 1 }}
                                            >
                                                Tải lên Dataset
                                            </Button>
                                        </Stack>
                                    </Paper>
                                ) : (
                                    <Grid container spacing={3}>
                                        {datasets.map((dataset) => (
                                            <Grid item xs={12} md={6} lg={4} key={dataset.id}>
                                                <Card
                                                    elevation={1}
                                                    sx={{
                                                        height: '100%',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        transition: 'all 0.3s',
                                                        '&:hover': {
                                                            boxShadow: 4,
                                                            transform: 'translateY(-4px)',
                                                            borderColor: 'primary.main'
                                                        }
                                                    }}
                                                >
                                                    <CardContent sx={{ p: 2.5 }}>
                                                        <Stack spacing={1.5}>
                                                            {/* Header */}
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="space-between"
                                                                alignItems="flex-start"
                                                                spacing={1}
                                                            >
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography
                                                                        variant="h5"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        {dataset.name}
                                                                    </Typography>
                                                                </Box>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Chip
                                                                        label={dataset.status}
                                                                        size="small"
                                                                        icon={
                                                                            dataset.status === 'ready' ? (
                                                                                <CheckCircle sx={{ fontSize: 16 }} />
                                                                            ) : dataset.status === 'error' ? (
                                                                                <ErrorIcon sx={{ fontSize: 16 }} />
                                                                            ) : (
                                                                                <HourglassEmpty sx={{ fontSize: 16 }} />
                                                                            )
                                                                        }
                                                                        color={
                                                                            dataset.status === 'ready'
                                                                                ? 'success'
                                                                                : dataset.status === 'error'
                                                                                  ? 'error'
                                                                                  : 'warning'
                                                                        }
                                                                        sx={{ fontWeight: 500 }}
                                                                    />
                                                                    <Tooltip title="Xóa dataset">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleDeleteClick(dataset)}
                                                                            sx={{
                                                                                '&:hover': {
                                                                                    bgcolor: 'error.lighter'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Stack>

                                                            {/* Type and Format */}
                                                            <Stack direction="row" spacing={1}>
                                                                <Chip
                                                                    label={dataset.dataset_type}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: 500 }}
                                                                />
                                                                <Chip
                                                                    label={dataset.format.toUpperCase()}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ fontWeight: 600 }}
                                                                />
                                                            </Stack>

                                                            {/* Statistics */}
                                                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                                <Grid container spacing={2}>
                                                                    <Grid item xs={6}>
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            <ImageIcon fontSize="small" color="primary" />
                                                                            <Box>
                                                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                                                    {dataset.total_images}
                                                                                </Typography>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    Images
                                                                                </Typography>
                                                                            </Box>
                                                                        </Stack>
                                                                    </Grid>
                                                                    <Grid item xs={6}>
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            <Label fontSize="small" color="secondary" />
                                                                            <Box>
                                                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                                                    {dataset.total_labels}
                                                                                </Typography>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    Labels
                                                                                </Typography>
                                                                            </Box>
                                                                        </Stack>
                                                                    </Grid>
                                                                </Grid>
                                                            </Paper>

                                                            {/* Classes */}
                                                            {dataset.classes && dataset.classes.length > 0 && (
                                                                <Box>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ fontWeight: 500 }}
                                                                    >
                                                                        Classes:{' '}
                                                                        <Typography component="span" variant="caption" color="text.primary">
                                                                            {dataset.classes.slice(0, 3).join(', ')}
                                                                            {dataset.classes.length > 3 &&
                                                                                ` (+${dataset.classes.length - 3})`}
                                                                        </Typography>
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            <Divider />

                                                            {/* Actions */}
                                                            <Stack direction="row" spacing={1.5}>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<Info />}
                                                                    onClick={() => handleViewDetails(dataset)}
                                                                    sx={{
                                                                        flex: 1,
                                                                        borderColor: 'divider',
                                                                        color: 'text.primary',
                                                                        '&:hover': {
                                                                            borderColor: 'primary.main',
                                                                            bgcolor: 'primary.lighter'
                                                                        }
                                                                    }}
                                                                >
                                                                    Chi tiết
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    disabled={dataset.status !== 'ready'}
                                                                    startIcon={<ModelTraining />}
                                                                    onClick={() => handleTrainClick(dataset)}
                                                                    sx={{
                                                                        flex: 1,
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    Train
                                                                </Button>
                                                            </Stack>

                                                            {/* Date */}
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ textAlign: 'center' }}
                                                            >
                                                                Tạo lúc: {new Date(dataset.created_at).toLocaleString('vi-VN')}
                                                            </Typography>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Stack>
                        </TabPanel>

                        {/* Training Jobs Tab */}
                        <TabPanel value={currentTab} index={1}>
                            <Stack spacing={2.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                                Training Jobs
                                            </Typography>
                                            {trainingJobs.some((job) => job.status === 'running' || job.status === 'pending') && (
                                                <Chip
                                                    icon={<Autorenew sx={{ animation: 'spin 2s linear infinite' }} />}
                                                    label="Auto-refreshing"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Quản lý training processes cho AI models. Tạo job từ tab Datasets.
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Divider />

                                {/* Content */}
                                {trainingJobs.length === 0 ? (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            py: 8,
                                            px: 4,
                                            textAlign: 'center',
                                            bgcolor: 'grey.50',
                                            borderRadius: 2,
                                            border: '2px dashed',
                                            borderColor: 'grey.300'
                                        }}
                                    >
                                        <Stack spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    bgcolor: 'secondary.lighter',
                                                    color: 'secondary.main'
                                                }}
                                            >
                                                <ModelTraining sx={{ fontSize: 40 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                                                    Chưa có training jobs
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                                                    Bắt đầu training AI models với datasets của bạn
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <Chip label="Object Detection" size="small" variant="outlined" />
                                                <Chip label="Classification" size="small" variant="outlined" />
                                                <Chip label="Segmentation" size="small" variant="outlined" />
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                ) : (
                                    <Grid container spacing={3}>
                                        {trainingJobs.map((job) => (
                                            <Grid item xs={12} md={6} lg={4} key={job.id}>
                                                <Card
                                                    elevation={1}
                                                    sx={{
                                                        height: '100%',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        transition: 'all 0.3s',
                                                        '&:hover': {
                                                            boxShadow: 4,
                                                            transform: 'translateY(-4px)',
                                                            borderColor: 'secondary.main'
                                                        }
                                                    }}
                                                >
                                                    <CardContent sx={{ p: 2.5 }}>
                                                        <Stack spacing={1.5}>
                                                            {/* Header */}
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="space-between"
                                                                alignItems="flex-start"
                                                                spacing={1}
                                                            >
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography
                                                                        variant="h5"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        {job.name}
                                                                    </Typography>
                                                                </Box>
                                                                <Chip
                                                                    label={job.status}
                                                                    size="small"
                                                                    color={
                                                                        job.status === 'completed'
                                                                            ? 'success'
                                                                            : job.status === 'failed'
                                                                              ? 'error'
                                                                              : job.status === 'running'
                                                                                ? 'primary'
                                                                                : 'warning'
                                                                    }
                                                                    sx={{ fontWeight: 500 }}
                                                                />
                                                            </Stack>

                                                            {/* Model & Framework */}
                                                            <Stack direction="row" spacing={1}>
                                                                <Chip
                                                                    label={job.framework}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: 500 }}
                                                                />
                                                                <Chip
                                                                    label={job.model_type.toUpperCase()}
                                                                    size="small"
                                                                    color="secondary"
                                                                    sx={{ fontWeight: 600 }}
                                                                />
                                                            </Stack>

                                                            {/* Progress Bar (if running) */}
                                                            {job.status === 'running' && (
                                                                <Box>
                                                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Progress
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                                            {job.progress || 0}%
                                                                        </Typography>
                                                                    </Stack>
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={job.progress || 0}
                                                                        sx={{ height: 6, borderRadius: 3 }}
                                                                    />
                                                                </Box>
                                                            )}

                                                            {/* Info */}
                                                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                                <Stack spacing={0.5}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Job ID:{' '}
                                                                        <Typography
                                                                            component="span"
                                                                            variant="caption"
                                                                            sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                                                                        >
                                                                            {job.id.substring(0, 8)}...
                                                                        </Typography>
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Dataset: <strong>{job.dataset_id.substring(0, 8)}...</strong>
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Container: <strong>{job.container_name}</strong>
                                                                    </Typography>
                                                                </Stack>
                                                            </Paper>

                                                            <Divider />

                                                            {/* Actions */}
                                                            <Stack direction="row" spacing={1}>
                                                                {job.status === 'pending' && (
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        startIcon={<PlayArrow />}
                                                                        onClick={() => handleStartJob(job)}
                                                                        sx={{
                                                                            flex: 1,
                                                                            bgcolor: 'success.main',
                                                                            '&:hover': {
                                                                                bgcolor: 'success.dark'
                                                                            }
                                                                        }}
                                                                    >
                                                                        Start
                                                                    </Button>
                                                                )}
                                                                {job.status === 'failed' && (
                                                                    <>
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            startIcon={<Refresh />}
                                                                            onClick={() => handleRestartJob(job)}
                                                                            sx={{
                                                                                flex: 1,
                                                                                bgcolor: 'warning.main',
                                                                                '&:hover': {
                                                                                    bgcolor: 'warning.dark'
                                                                                }
                                                                            }}
                                                                        >
                                                                            Restart
                                                                        </Button>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="error"
                                                                            startIcon={<DeleteIcon />}
                                                                            onClick={() => handleDeleteJob(job)}
                                                                            sx={{
                                                                                flex: 1
                                                                            }}
                                                                        >
                                                                            Xóa
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                {job.status !== 'failed' && (
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<Visibility />}
                                                                        onClick={() => handleViewJobDetails(job)}
                                                                        sx={{
                                                                            flex: job.status === 'pending' ? 1 : undefined,
                                                                            width: job.status === 'pending' ? 'auto' : '100%',
                                                                            borderColor: 'divider',
                                                                            color: 'text.primary',
                                                                            '&:hover': {
                                                                                borderColor: 'secondary.main',
                                                                                bgcolor: 'secondary.lighter'
                                                                            }
                                                                        }}
                                                                    >
                                                                        Chi tiết
                                                                    </Button>
                                                                )}
                                                            </Stack>

                                                            {/* Date */}
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ textAlign: 'center' }}
                                                            >
                                                                Tạo lúc: {new Date(job.created_at).toLocaleString('vi-VN')}
                                                            </Typography>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Stack>
                        </TabPanel>

                        {/* Models Tab */}
                        <TabPanel value={currentTab} index={2}>
                            <Stack spacing={2.5}>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                        Trained Models
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Models đã train thành công và sẵn sàng deploy
                                    </Typography>
                                </Box>

                                <Divider />

                                {models.length === 0 ? (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            py: 8,
                                            px: 4,
                                            textAlign: 'center',
                                            bgcolor: 'grey.50',
                                            borderRadius: 2,
                                            border: '2px dashed',
                                            borderColor: 'grey.300'
                                        }}
                                    >
                                        <Stack spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    bgcolor: 'success.lighter',
                                                    color: 'success.main'
                                                }}
                                            >
                                                <Psychology sx={{ fontSize: 40 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                                                    Chưa có models
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                                                    Models sẽ xuất hiện sau khi training jobs hoàn thành
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                ) : (
                                    <Grid container spacing={3}>
                                        {models.map((model) => (
                                            <Grid item xs={12} md={6} lg={4} key={model.id}>
                                                <Card
                                                    elevation={1}
                                                    sx={{
                                                        height: '100%',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        transition: 'all 0.3s',
                                                        '&:hover': {
                                                            boxShadow: 4,
                                                            transform: 'translateY(-4px)',
                                                            borderColor: 'success.main'
                                                        }
                                                    }}
                                                >
                                                    <CardContent sx={{ p: 2.5 }}>
                                                        <Stack spacing={1.5}>
                                                            {/* Header */}
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="space-between"
                                                                alignItems="flex-start"
                                                                spacing={1}
                                                            >
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography
                                                                        variant="h5"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        {model.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        v{model.version}
                                                                    </Typography>
                                                                </Box>
                                                                <Chip
                                                                    label={
                                                                        !model.is_deployed
                                                                            ? 'Ready'
                                                                            : containerStatuses[model.id]?.status === 'running'
                                                                              ? 'Running'
                                                                              : containerStatuses[model.id]?.status === 'stopped'
                                                                                ? 'Stopped'
                                                                                : 'Deployed'
                                                                    }
                                                                    size="small"
                                                                    color={
                                                                        !model.is_deployed
                                                                            ? 'default'
                                                                            : containerStatuses[model.id]?.status === 'running'
                                                                              ? 'success'
                                                                              : containerStatuses[model.id]?.status === 'stopped'
                                                                                ? 'warning'
                                                                                : 'info'
                                                                    }
                                                                    sx={{ fontWeight: 500 }}
                                                                />
                                                            </Stack>

                                                            {/* Model & Framework */}
                                                            <Stack direction="row" spacing={1}>
                                                                <Chip
                                                                    label={model.framework}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: 500 }}
                                                                />
                                                                <Chip
                                                                    label={model.model_type.toUpperCase()}
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{ fontWeight: 600 }}
                                                                />
                                                            </Stack>

                                                            {/* Metrics */}
                                                            {model.metrics && (
                                                                <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                                    <Grid container spacing={1}>
                                                                        {model.metrics.mAP50 && (
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    mAP50
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                    {(parseFloat(model.metrics.mAP50) * 100).toFixed(1)}%
                                                                                </Typography>
                                                                            </Grid>
                                                                        )}
                                                                        {model.metrics['mAP50-95'] && (
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    mAP50-95
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                    {(parseFloat(model.metrics['mAP50-95']) * 100).toFixed(
                                                                                        1
                                                                                    )}
                                                                                    %
                                                                                </Typography>
                                                                            </Grid>
                                                                        )}
                                                                        {model.metrics.precision && (
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    Precision
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                    {(parseFloat(model.metrics.precision) * 100).toFixed(1)}
                                                                                    %
                                                                                </Typography>
                                                                            </Grid>
                                                                        )}
                                                                        {model.metrics.recall && (
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    Recall
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                    {(parseFloat(model.metrics.recall) * 100).toFixed(1)}%
                                                                                </Typography>
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </Paper>
                                                            )}

                                                            {/* Classes */}
                                                            {model.classes && model.classes.length > 0 && (
                                                                <Box>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ fontWeight: 500 }}
                                                                    >
                                                                        Classes:{' '}
                                                                        <Typography component="span" variant="caption" color="text.primary">
                                                                            {model.classes.slice(0, 3).join(', ')}
                                                                            {model.classes.length > 3 && ` (+${model.classes.length - 3})`}
                                                                        </Typography>
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            <Divider />

                                                            {/* Actions */}
                                                            <Stack direction="row" spacing={1}>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<Info />}
                                                                    onClick={() => handleViewModelDetails(model)}
                                                                    sx={{
                                                                        flex: 1,
                                                                        borderColor: 'divider',
                                                                        color: 'text.primary',
                                                                        '&:hover': {
                                                                            borderColor: 'success.main',
                                                                            bgcolor: 'success.lighter'
                                                                        }
                                                                    }}
                                                                >
                                                                    Chi tiết
                                                                </Button>

                                                                {/* Deployment Controls */}
                                                                {!model.is_deployed ? (
                                                                    // Not deployed: Show Deploy button
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="success"
                                                                        startIcon={<PlayArrow />}
                                                                        onClick={() => handleDeployModel(model)}
                                                                        sx={{ flex: 1, fontWeight: 600 }}
                                                                    >
                                                                        Deploy
                                                                    </Button>
                                                                ) : (
                                                                    // Deployed: Show Start/Stop based on container status
                                                                    <>
                                                                        {containerStatuses[model.id]?.status === 'running' ? (
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                color="warning"
                                                                                startIcon={<Stop />}
                                                                                onClick={() => handleStopContainer(model)}
                                                                                sx={{ flex: 1, fontWeight: 600 }}
                                                                            >
                                                                                Stop
                                                                            </Button>
                                                                        ) : (
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                color="info"
                                                                                startIcon={<PlayArrow />}
                                                                                onClick={() => handleStartContainer(model)}
                                                                                sx={{ flex: 1, fontWeight: 600 }}
                                                                            >
                                                                                Start
                                                                            </Button>
                                                                        )}
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="error"
                                                                            startIcon={<PowerSettingsNew />}
                                                                            onClick={() => handleUndeployModel(model)}
                                                                            sx={{ flex: 1, fontWeight: 600 }}
                                                                        >
                                                                            Undeploy
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </Stack>

                                                            {/* Delete Button */}
                                                            {!model.is_deployed && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="error"
                                                                    startIcon={<DeleteIcon />}
                                                                    onClick={() => handleDeleteModel(model)}
                                                                    sx={{
                                                                        width: '100%'
                                                                    }}
                                                                >
                                                                    Xóa Model
                                                                </Button>
                                                            )}

                                                            {/* Date */}
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ textAlign: 'center' }}
                                                            >
                                                                Tạo lúc: {new Date(model.created_at).toLocaleString('vi-VN')}
                                                            </Typography>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Stack>
                        </TabPanel>

                        {/* Inference Tab */}
                        <TabPanel value={currentTab} index={3}>
                            <Stack spacing={2.5}>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                        API Documentation & Inference
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        API documentation và hướng dẫn sử dụng cho deployed models
                                    </Typography>
                                </Box>

                                <Divider />

                                {models.filter((m) => m.is_deployed).length === 0 ? (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            py: 8,
                                            px: 4,
                                            textAlign: 'center',
                                            bgcolor: 'grey.50',
                                            borderRadius: 2,
                                            border: '2px dashed',
                                            borderColor: 'grey.300'
                                        }}
                                    >
                                        <Stack spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    bgcolor: 'warning.lighter',
                                                    color: 'warning.main'
                                                }}
                                            >
                                                <Visibility sx={{ fontSize: 40 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                                                    Chưa có deployed models
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                                                    Deploy models từ tab Models để xem API documentation
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                ) : (
                                    <Grid container spacing={3}>
                                        {/* Left: Model List */}
                                        <Grid item xs={12} md={4}>
                                            <Paper
                                                elevation={0}
                                                sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                                    Deployed Models ({models.filter((m) => m.is_deployed).length})
                                                </Typography>
                                                <Stack spacing={1.5}>
                                                    {models
                                                        .filter((m) => m.is_deployed)
                                                        .map((model) => {
                                                            const containerStatus = containerStatuses[model.id];
                                                            const isSelected = selectedInferenceModel?.id === model.id;

                                                            return (
                                                                <Card
                                                                    key={model.id}
                                                                    elevation={isSelected ? 3 : 1}
                                                                    onClick={() => {
                                                                        setSelectedInferenceModel(model);
                                                                        setInferenceImage(null);
                                                                        setInferenceResult(null);
                                                                    }}
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        border: '2px solid',
                                                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                                                        bgcolor: isSelected ? 'primary.lighter' : 'background.paper',
                                                                        transition: 'all 0.2s',
                                                                        '&:hover': {
                                                                            borderColor: 'primary.main',
                                                                            transform: 'translateX(4px)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                                        <Stack spacing={1}>
                                                                            <Stack
                                                                                direction="row"
                                                                                justifyContent="space-between"
                                                                                alignItems="flex-start"
                                                                            >
                                                                                <Typography
                                                                                    variant="subtitle1"
                                                                                    sx={{ fontWeight: 600, flex: 1 }}
                                                                                >
                                                                                    {model.name}
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={
                                                                                        containerStatus?.status === 'running'
                                                                                            ? 'Running'
                                                                                            : 'Stopped'
                                                                                    }
                                                                                    size="small"
                                                                                    color={
                                                                                        containerStatus?.status === 'running'
                                                                                            ? 'success'
                                                                                            : 'warning'
                                                                                    }
                                                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                                                />
                                                                            </Stack>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {model.model_type.toUpperCase()} · v{model.version}
                                                                            </Typography>
                                                                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                                                                <Chip
                                                                                    label={model.framework}
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                                                />
                                                                                <Chip
                                                                                    label={`${model.classes?.length || 0} classes`}
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                                                />
                                                                            </Stack>
                                                                        </Stack>
                                                                    </CardContent>
                                                                </Card>
                                                            );
                                                        })}
                                                </Stack>
                                            </Paper>
                                        </Grid>

                                        {/* Right: Model Details & Test Interface */}
                                        <Grid item xs={12} md={8}>
                                            {!selectedInferenceModel ? (
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 8,
                                                        textAlign: 'center',
                                                        border: '2px dashed',
                                                        borderColor: 'grey.300',
                                                        borderRadius: 2,
                                                        bgcolor: 'grey.50'
                                                    }}
                                                >
                                                    <Stack spacing={2} alignItems="center">
                                                        <Visibility sx={{ fontSize: 60, color: 'text.secondary' }} />
                                                        <Typography variant="h6" color="text.secondary">
                                                            Chọn model từ danh sách bên trái
                                                        </Typography>
                                                    </Stack>
                                                </Paper>
                                            ) : (
                                                <Stack spacing={2}>
                                                    {/* Test Interface */}
                                                    <Card elevation={1} sx={{ border: '1px solid', borderColor: 'divider' }}>
                                                        <CardContent sx={{ p: 3 }}>
                                                            <Stack spacing={2.5}>
                                                                <Stack direction="row" spacing={2} alignItems="center">
                                                                    <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                                                        <Psychology />
                                                                    </Avatar>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                                            {selectedInferenceModel.name}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {selectedInferenceModel.model_type.toUpperCase()} · Version{' '}
                                                                            {selectedInferenceModel.version}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Chip
                                                                        label={
                                                                            containerStatuses[selectedInferenceModel.id]?.status ===
                                                                            'running'
                                                                                ? '● Online'
                                                                                : '○ Offline'
                                                                        }
                                                                        color={
                                                                            containerStatuses[selectedInferenceModel.id]?.status ===
                                                                            'running'
                                                                                ? 'success'
                                                                                : 'warning'
                                                                        }
                                                                        size="small"
                                                                    />
                                                                </Stack>

                                                                <Divider />

                                                                {/* Image Upload */}
                                                                <Box>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                                                        📷 Upload Image
                                                                    </Typography>
                                                                    <input
                                                                        accept="image/*"
                                                                        style={{ display: 'none' }}
                                                                        id="inference-image-upload"
                                                                        type="file"
                                                                        onChange={handleImageUpload}
                                                                    />
                                                                    <label htmlFor="inference-image-upload">
                                                                        <Paper
                                                                            elevation={0}
                                                                            sx={{
                                                                                p: 3,
                                                                                textAlign: 'center',
                                                                                bgcolor: inferenceImage ? 'success.lighter' : 'grey.50',
                                                                                borderRadius: 1,
                                                                                border: '2px dashed',
                                                                                borderColor: inferenceImage ? 'success.main' : 'grey.300',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s',
                                                                                '&:hover': {
                                                                                    borderColor: 'primary.main',
                                                                                    bgcolor: 'primary.lighter'
                                                                                }
                                                                            }}
                                                                            component="div"
                                                                        >
                                                                            <Stack spacing={1} alignItems="center">
                                                                                {inferenceImage ? (
                                                                                    <>
                                                                                        <CheckCircle
                                                                                            sx={{ fontSize: 40, color: 'success.main' }}
                                                                                        />
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{ fontWeight: 600 }}
                                                                                        >
                                                                                            {inferenceImage.name}
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            color="text.secondary"
                                                                                        >
                                                                                            Click để thay đổi ảnh
                                                                                        </Typography>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <PhotoCamera
                                                                                            sx={{ fontSize: 40, color: 'text.secondary' }}
                                                                                        />
                                                                                        <Typography variant="body2" color="text.secondary">
                                                                                            Click để upload ảnh
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            color="text.secondary"
                                                                                        >
                                                                                            Supported: JPG, PNG
                                                                                        </Typography>
                                                                                    </>
                                                                                )}
                                                                            </Stack>
                                                                        </Paper>
                                                                    </label>
                                                                </Box>

                                                                {/* Run Button */}
                                                                <Button
                                                                    variant="contained"
                                                                    size="large"
                                                                    fullWidth
                                                                    startIcon={
                                                                        inferenceLoading ? (
                                                                            <Autorenew
                                                                                sx={{
                                                                                    animation: 'spin 1s linear infinite',
                                                                                    '@keyframes spin': {
                                                                                        '0%': { transform: 'rotate(0deg)' },
                                                                                        '100%': { transform: 'rotate(360deg)' }
                                                                                    }
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <PlayArrow />
                                                                        )
                                                                    }
                                                                    onClick={handleRunInference}
                                                                    disabled={
                                                                        !inferenceImage ||
                                                                        inferenceLoading ||
                                                                        containerStatuses[selectedInferenceModel.id]?.status !== 'running'
                                                                    }
                                                                    sx={{
                                                                        py: 1.5,
                                                                        fontWeight: 600,
                                                                        fontSize: '1rem'
                                                                    }}
                                                                >
                                                                    {inferenceLoading ? 'Running...' : 'Run Inference'}
                                                                </Button>

                                                                {containerStatuses[selectedInferenceModel.id]?.status !== 'running' && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="warning.main"
                                                                        sx={{ textAlign: 'center', fontWeight: 600 }}
                                                                    >
                                                                        ⚠️ Model container is not running. Please start it from Models tab.
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>

                                                    {/* Results Display */}
                                                    {inferenceResult && (
                                                        <Card elevation={1} sx={{ border: '1px solid', borderColor: 'divider' }}>
                                                            <CardContent sx={{ p: 3 }}>
                                                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                                                    📊 Inference Results
                                                                </Typography>

                                                                {/* Annotated Image */}
                                                                {annotatedImageUrl && (
                                                                    <Box sx={{ mb: 3 }}>
                                                                        <Paper
                                                                            elevation={2}
                                                                            sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}
                                                                        >
                                                                            <img
                                                                                src={annotatedImageUrl}
                                                                                alt="Annotated result"
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: 'auto',
                                                                                    borderRadius: '8px',
                                                                                    display: 'block'
                                                                                }}
                                                                            />
                                                                        </Paper>
                                                                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                startIcon={<Download />}
                                                                                onClick={() => {
                                                                                    const a = document.createElement('a');
                                                                                    a.href = annotatedImageUrl;
                                                                                    a.download = `inference_result_${Date.now()}.jpg`;
                                                                                    a.click();
                                                                                }}
                                                                            >
                                                                                Download Result
                                                                            </Button>
                                                                        </Stack>
                                                                    </Box>
                                                                )}

                                                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                                                    <Grid item xs={4}>
                                                                        <Paper
                                                                            elevation={0}
                                                                            sx={{
                                                                                p: 1.5,
                                                                                bgcolor: 'grey.50',
                                                                                borderRadius: 1,
                                                                                textAlign: 'center'
                                                                            }}
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Detections
                                                                            </Typography>
                                                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                                                {inferenceResult.detections?.length || 0}
                                                                            </Typography>
                                                                        </Paper>
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <Paper
                                                                            elevation={0}
                                                                            sx={{
                                                                                p: 1.5,
                                                                                bgcolor: 'grey.50',
                                                                                borderRadius: 1,
                                                                                textAlign: 'center'
                                                                            }}
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Inference Time
                                                                            </Typography>
                                                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                                                {inferenceResult.inference_time_ms?.toFixed(1) || 0} ms
                                                                            </Typography>
                                                                        </Paper>
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <Paper
                                                                            elevation={0}
                                                                            sx={{
                                                                                p: 1.5,
                                                                                bgcolor: 'grey.50',
                                                                                borderRadius: 1,
                                                                                textAlign: 'center'
                                                                            }}
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Image Size
                                                                            </Typography>
                                                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                                                {inferenceResult.image_size?.join(' × ')}
                                                                            </Typography>
                                                                        </Paper>
                                                                    </Grid>
                                                                </Grid>

                                                                {/* Detection Details */}
                                                                {inferenceResult.detections && inferenceResult.detections.length > 0 && (
                                                                    <Box sx={{ mt: 2 }}>
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                                            Detected Objects
                                                                        </Typography>
                                                                        <Stack spacing={1}>
                                                                            {inferenceResult.detections.map((det, idx) => (
                                                                                <Paper
                                                                                    key={idx}
                                                                                    elevation={0}
                                                                                    sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}
                                                                                >
                                                                                    <Stack
                                                                                        direction="row"
                                                                                        justifyContent="space-between"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <Stack
                                                                                            direction="row"
                                                                                            spacing={1}
                                                                                            alignItems="center"
                                                                                        >
                                                                                            <Box
                                                                                                sx={{
                                                                                                    width: 12,
                                                                                                    height: 12,
                                                                                                    borderRadius: '50%',
                                                                                                    bgcolor: `hsl(${(idx * 137.5) % 360}, 70%, 50%)`
                                                                                                }}
                                                                                            />
                                                                                            <Typography
                                                                                                variant="body2"
                                                                                                sx={{ fontWeight: 600 }}
                                                                                            >
                                                                                                {det.class_name}
                                                                                            </Typography>
                                                                                        </Stack>
                                                                                        <Chip
                                                                                            label={`${(det.confidence * 100).toFixed(1)}%`}
                                                                                            size="small"
                                                                                            color={
                                                                                                det.confidence > 0.8
                                                                                                    ? 'success'
                                                                                                    : det.confidence > 0.5
                                                                                                      ? 'warning'
                                                                                                      : 'default'
                                                                                            }
                                                                                        />
                                                                                    </Stack>
                                                                                </Paper>
                                                                            ))}
                                                                        </Stack>
                                                                    </Box>
                                                                )}

                                                                {/* JSON Details (Collapsible) */}
                                                                <Box sx={{ mt: 2 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            cursor: 'pointer',
                                                                            color: 'primary.main',
                                                                            textDecoration: 'underline'
                                                                        }}
                                                                        onClick={() => {
                                                                            const elem = document.getElementById('json-details');
                                                                            if (elem)
                                                                                elem.style.display =
                                                                                    elem.style.display === 'none' ? 'block' : 'none';
                                                                        }}
                                                                    >
                                                                        Show JSON Details
                                                                    </Typography>
                                                                    <Paper
                                                                        id="json-details"
                                                                        elevation={0}
                                                                        sx={{
                                                                            p: 2,
                                                                            bgcolor: 'grey.900',
                                                                            borderRadius: 1,
                                                                            mt: 1,
                                                                            display: 'none'
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="caption"
                                                                            component="pre"
                                                                            sx={{
                                                                                color: '#00ff00',
                                                                                fontFamily: 'monospace',
                                                                                whiteSpace: 'pre-wrap',
                                                                                m: 0,
                                                                                maxHeight: 300,
                                                                                overflow: 'auto'
                                                                            }}
                                                                        >
                                                                            {JSON.stringify(inferenceResult, null, 2)}
                                                                        </Typography>
                                                                    </Paper>
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    )}

                                                    {/* API Documentation (Collapsible) */}
                                                    <Card elevation={1} sx={{ border: '1px solid', borderColor: 'divider' }}>
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="space-between"
                                                                alignItems="center"
                                                                onClick={() => setShowApiDocs(!showApiDocs)}
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                                    📚 API Documentation
                                                                </Typography>
                                                                <IconButton size="small">
                                                                    {showApiDocs ? (
                                                                        <ArrowBack sx={{ transform: 'rotate(90deg)' }} />
                                                                    ) : (
                                                                        <ArrowBack sx={{ transform: 'rotate(-90deg)' }} />
                                                                    )}
                                                                </IconButton>
                                                            </Stack>
                                                            {showApiDocs && (
                                                                <Box sx={{ mt: 2 }}>
                                                                    {(() => {
                                                                        const shortId = selectedInferenceModel.id.substring(0, 8);
                                                                        const baseUrl =
                                                                            selectedInferenceModel.deployment_url ||
                                                                            `https://datahub.n2nai.io/models/${shortId}`;
                                                                        return (
                                                                            <Stack spacing={2}>
                                                                                <Divider />
                                                                                {/* Endpoints */}
                                                                                <Box>
                                                                                    <Typography
                                                                                        variant="subtitle2"
                                                                                        sx={{ fontWeight: 600, mb: 1.5 }}
                                                                                    >
                                                                                        Endpoints
                                                                                    </Typography>
                                                                                    <Stack spacing={1}>
                                                                                        <Paper
                                                                                            elevation={0}
                                                                                            sx={{
                                                                                                p: 1.5,
                                                                                                bgcolor: 'grey.50',
                                                                                                borderRadius: 1
                                                                                            }}
                                                                                        >
                                                                                            <Stack
                                                                                                direction="row"
                                                                                                spacing={1}
                                                                                                alignItems="center"
                                                                                            >
                                                                                                <Chip
                                                                                                    label="GET"
                                                                                                    size="small"
                                                                                                    color="info"
                                                                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                                                                />
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    sx={{
                                                                                                        fontFamily: 'monospace',
                                                                                                        fontWeight: 600
                                                                                                    }}
                                                                                                >
                                                                                                    {baseUrl}/health
                                                                                                </Typography>
                                                                                            </Stack>
                                                                                        </Paper>
                                                                                        <Paper
                                                                                            elevation={0}
                                                                                            sx={{
                                                                                                p: 1.5,
                                                                                                bgcolor: 'grey.50',
                                                                                                borderRadius: 1
                                                                                            }}
                                                                                        >
                                                                                            <Stack
                                                                                                direction="row"
                                                                                                spacing={1}
                                                                                                alignItems="center"
                                                                                            >
                                                                                                <Chip
                                                                                                    label="POST"
                                                                                                    size="small"
                                                                                                    color="success"
                                                                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                                                                />
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    sx={{
                                                                                                        fontFamily: 'monospace',
                                                                                                        fontWeight: 600
                                                                                                    }}
                                                                                                >
                                                                                                    {baseUrl}/predict
                                                                                                </Typography>
                                                                                            </Stack>
                                                                                        </Paper>
                                                                                    </Stack>
                                                                                </Box>
                                                                                {/* Example curl */}
                                                                                <Box>
                                                                                    <Typography
                                                                                        variant="subtitle2"
                                                                                        sx={{ fontWeight: 600, mb: 1 }}
                                                                                    >
                                                                                        Example (cURL)
                                                                                    </Typography>
                                                                                    <Paper
                                                                                        elevation={0}
                                                                                        sx={{
                                                                                            p: 1.5,
                                                                                            bgcolor: 'grey.900',
                                                                                            borderRadius: 1
                                                                                        }}
                                                                                    >
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            component="pre"
                                                                                            sx={{
                                                                                                color: '#00ff00',
                                                                                                fontFamily: 'monospace',
                                                                                                whiteSpace: 'pre-wrap',
                                                                                                wordBreak: 'break-all',
                                                                                                m: 0
                                                                                            }}
                                                                                        >
                                                                                            {`curl -X POST ${baseUrl}/predict \\
  -F "file=@image.jpg" \\
  -F "conf=0.25"`}
                                                                                        </Typography>
                                                                                    </Paper>
                                                                                </Box>
                                                                            </Stack>
                                                                        );
                                                                    })()}
                                                                </Box>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Stack>
                                            )}
                                        </Grid>
                                    </Grid>
                                )}
                            </Stack>
                        </TabPanel>
                    </Card>
                </Box>
            </Stack>

            {/* Upload Dialog */}
            <DatasetUploadDialog
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                onSuccess={handleUploadSuccess}
                projectName={projectName}
                tenantId={currentUser?.tenant_id}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Warning color="warning" sx={{ fontSize: 32 }} />
                        <Typography variant="h4" component="span">
                            Xác nhận xóa dataset
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Bạn có chắc chắn muốn xóa dataset <strong>"{datasetToDelete?.name}"</strong>?
                        <br />
                        <br />
                        Dataset sẽ được đánh dấu là đã xóa và không hiển thị trong danh sách nữa. Dữ liệu vẫn được lưu trữ trong hệ thống và
                        có thể khôi phục nếu cần.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleDeleteCancel} variant="outlined">
                        Hủy
                    </Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" color="error" startIcon={<DeleteIcon />} autoFocus>
                        Xóa dataset
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dataset Details Dialog */}
            <DatasetDetailsDialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} datasetId={selectedDatasetId} />
            {/* Training Configuration Dialog */}
            <TrainingConfigDialog
                open={trainingDialogOpen}
                onClose={handleTrainCancel}
                onConfirm={handleTrainConfirm}
                dataset={datasetToTrain}
                projectId={projectName}
            />

            {/* Training Job Details Dialog */}
            <TrainingJobDetailsDialog
                open={trainingJobDetailsOpen}
                onClose={() => setTrainingJobDetailsOpen(false)}
                job={selectedTrainingJob}
            />

            {/* Model Details Dialog */}
            <ModelDetailsDialog open={modelDetailsOpen} onClose={() => setModelDetailsOpen(false)} model={selectedModel} />
        </MainCard>
    );
};

export default CVModulePage;
